import { useState, useEffect, useRef, useCallback } from 'react';
import { getWsToken } from '../lib/api.js';

const MAX_BACKOFF = 60000;
const BASE_DELAY = 2000;
const MAX_RETRIES = 10;

export function useRealtime(agentId) {
  const [connectionState, setConnectionState] = useState('disconnected'); // 'connecting' | 'connected' | 'disconnected'
  const [agentStatus, setAgentStatus] = useState('unknown'); // 'online' | 'offline' | 'unknown'
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const timerRef = useRef(null);
  const everConnectedRef = useRef(false);

  const connect = useCallback(() => {
    if (!agentId) return;

    // Stop retrying after MAX_RETRIES if we never successfully connected
    if (!everConnectedRef.current && retriesRef.current >= MAX_RETRIES) {
      return;
    }

    setConnectionState('connecting');

    // In production, API may be cross-origin (api.vista-cloud.io).
    // Use VITE_API_BASE if set, otherwise same-origin.
    const apiBase = import.meta.env.VITE_API_BASE || window.location.origin;
    const apiHost = new URL(apiBase).host;
    const wsBase = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${apiHost}`;
    const token = getWsToken();
    const url = `${wsBase}/ws?agent_id=${encodeURIComponent(agentId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionState('connected');
      retriesRef.current = 0;
      everConnectedRef.current = true;
      // No application-level ping/pong — WebSocket protocol keepalive is
      // handled by Cloudflare's edge. The onclose handler detects drops.
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'agent.status') {
          setAgentStatus(data.status);
        } else {
          setLastEvent(data);
        }
      } catch {
        setLastEvent(event.data);
      }
    };

    ws.onclose = () => {
      setConnectionState('disconnected');
      const delay = Math.min(BASE_DELAY * 2 ** retriesRef.current, MAX_BACKOFF);
      retriesRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [agentId]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(timerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected: connectionState === 'connected', connectionState, agentStatus, lastEvent };
}
